import { useState, useCallback } from "react";

import {
  TextInputField,
  UserRegistrationForm,
} from "../types/userRegistrationForm";
import { ValidationCriterion } from "../types/validationCriterion";
import {
  RegistrationInputValidation,
  RegistrationInputValidationMap,
} from "../types/userRegistrationForm";

import {
  validateUsernameConditions,
  validatePasswordConditions,
  validatePasswordConfirmationConditions,
  validateEmailStringConditions,
} from "pages/register/util/fieldValidators";

const getErrorMessage = (inputField: TextInputField) => {
  switch (inputField) {
    case TextInputField.Email:
      return "This is an invalid email address.";
    case TextInputField.PasswordConfirmation:
      return "Those passwords don't match. Try again.";
  }
};

const defaultRegistrationValidationMap = () =>
  Object.values(TextInputField).reduce((validationMap, field) => {
    validationMap[field] = {
      isValid: false,
      validations: [],
    };

    return validationMap;
  }, {} as RegistrationInputValidationMap);

const useRegistrationInputValidationManager = () => {
  const [inputValidationMap, setInputValidationMap] =
    useState<RegistrationInputValidationMap>(
      defaultRegistrationValidationMap(),
    );

  const updateInputValidation = (
    inputField: TextInputField,
    value: string,
    currentData: UserRegistrationForm,
  ): Partial<RegistrationInputValidationMap> => {
    const validationsMap: Partial<
      Record<TextInputField, ValidationCriterion[]>
    > = {};

    // Build a map with a new list of validations for the given input
    switch (inputField) {
      case TextInputField.Email:
        validationsMap[inputField] = validateEmailStringConditions(value);
        break;
      case TextInputField.Username:
        validationsMap[inputField] = validateUsernameConditions(value);

        // Password needs to be revalidated due to the requirement that it cannot contain the username
        validationsMap[TextInputField.Password] = validatePasswordConditions(
          currentData.password,
          value,
        );
        break;
      case TextInputField.Password:
        validationsMap[inputField] = validatePasswordConditions(
          value,
          currentData.username,
        );

        if (currentData.passwordConfirmation) {
          validationsMap[TextInputField.PasswordConfirmation] =
            validatePasswordConfirmationConditions(
              value,
              currentData.passwordConfirmation,
            );
        }

        break;
      case TextInputField.PasswordConfirmation:
        validationsMap[inputField] = validatePasswordConfirmationConditions(
          currentData.password,
          value,
        );
        break;
    }

    // Use the validations map to build the final input validation map that includes isValid and errorMessage
    const updatedValidationMap = Object.entries(validationsMap).reduce(
      (map, [key, validations]) => {
        const inputField = key as TextInputField;
        const updatedInputValidation: RegistrationInputValidation = {
          isValid: validations.every((validation) => validation.isValid),
          validations,
        };

        // Do not set the inline error message if the input is empty
        if (!updatedInputValidation.isValid && value) {
          updatedInputValidation.errorMessage = getErrorMessage(inputField);
        }

        map[inputField] = updatedInputValidation;

        return map;
      },
      {} as Partial<RegistrationInputValidationMap>,
    );

    setInputValidationMap((prevData) => ({
      ...prevData,
      ...updatedValidationMap,
    }));

    return updatedValidationMap;
  };

  const clearInputValidation = useCallback(
    () => setInputValidationMap(defaultRegistrationValidationMap()),
    [],
  );

  return {
    inputValidationMap,
    updateInputValidation,
    clearInputValidation,
  };
};

export default useRegistrationInputValidationManager;
